import { Application, Assets, Texture } from 'pixi.js';
import { Reel } from './Reel';
import { Paylines } from './Paylines';
import { WinAnimator } from './WinAnimator';
import { GAME_CONFIG, getAllSymbols, SymbolData } from '../config/GameConfig';
import { getSymbolAssets, getUIAssets } from '../config/AssetList';
import type { Pinia } from 'pinia';
import { useGameStore } from '../stores/gameStore';

export class Game {
  private app: Application;
  private gameStore: ReturnType<typeof useGameStore>;
  private reels: Reel[] = [];
  private paylines: Paylines;
  private winAnimator: WinAnimator;
  private textures: Map<string, Texture> = new Map();
  private frameTexture: Texture | null = null;
  
  private currentBet: number = 10;
  private activePaylines: number = 20;
  private spinning: boolean = false;
  
  private spinCompleteCallback?: () => void;
  
  constructor(app: Application, pinia: Pinia) {
    this.app = app;
    this.gameStore = useGameStore(pinia);
    this.paylines = new Paylines();
    this.winAnimator = new WinAnimator(app.stage);
  }
  
  public async init(): Promise<void> {
    await this.loadAssets();
    this.createReels();
    this.positionReels();
  }
  
  private async loadAssets(): Promise<void> {
    const symbolAssets = getSymbolAssets();
    const uiAssets = getUIAssets();
    
    console.log('=== LOADING TEXTURES ===');
    console.log('Loading symbols...');
    for (const [key, path] of Object.entries(symbolAssets)) {
      try {
        console.log(`Loading: ${key} from ${path}`);
        const texture = await Assets.load(path as string);
        this.textures.set(key, texture);
        console.log(`✓ Loaded texture: ${key} → ${path}`);
      } catch (error) {
        console.error(`✗ Failed to load asset: ${path}`, error);
      }
    }
    
    console.log('\nLoading UI assets...');
    for (const [key, path] of Object.entries(uiAssets)) {
      try {
        console.log(`Loading UI: ${key} from ${path}`);
        const texture = await Assets.load(path as string);
        this.textures.set(`ui_${key}`, texture);
        console.log(`✓ Loaded UI texture: ${key} → ${path}`);
      } catch (error) {
        console.error(`✗ Failed to load UI asset: ${path}`, error);
      }
    }
    
    // Store frame texture separately
    this.frameTexture = this.textures.get('ui_frameBackground') || null;
    if (this.frameTexture) {
      console.log('✓ Frame background loaded');
    } else {
      console.warn('Frame background not loaded, using fallback');
    }
    
    console.log(`\nTotal textures loaded: ${this.textures.size}`);
    console.log('Texture keys:', Array.from(this.textures.keys()));
    
    const allSymbols = getAllSymbols();
    console.log('All symbols from config:', allSymbols.map(s => `${s.id}:${s.name}`));
    
    const availableSymbols = allSymbols.filter(s => this.textures.has(s.name));
    
    console.log(`\nAvailable symbols: ${availableSymbols.length}/${allSymbols.length}`);
    console.log('Available symbol names:', availableSymbols.map(s => s.name));
    
    if (availableSymbols.length < 3) {
      console.error('Not enough symbols loaded!');
    }
  }
  
  public getFrameTexture(): Texture | null {
    return this.frameTexture;
  }
  
  private createReels(): void {
    const allSymbols = getAllSymbols().filter(s => this.textures.has(s.name));
    
    for (let i = 0; i < GAME_CONFIG.REELS_COUNT; i++) {
      const reel = new Reel(i, allSymbols, this.textures);
      this.reels.push(reel);
      reel.zIndex = 1;
      this.app.stage.addChild(reel);
    }
  }
  
  private positionReels(): void {
    const { SYMBOL_SIZE, REEL_SPACING, REELS_COUNT } = GAME_CONFIG;
    const reelWidth = SYMBOL_SIZE + REEL_SPACING;
    const totalWidth = reelWidth * REELS_COUNT;
    const startX = -totalWidth / 2 + reelWidth / 2;
    
    this.reels.forEach((reel, index) => {
      reel.x = startX + index * reelWidth;
      reel.y = -50; // Move up by 50 pixels to match frame position
    });
  }
  
  /**
   * Spin with backend reels (for real play)
   */
  public spinWithReels(backendReels: number[][]): void {
    if (this.spinning) return;
    
    this.spinning = true;
    this.paylines.clear();
    this.winAnimator.clear();
    
    // Convert backend reels to symbol data
    const symbolData = this.reelsToSymbolData(backendReels);
    const allSymbols = getAllSymbols().filter(s => this.textures.has(s.name));
    
    // Spin each reel with the backend result
    this.reels.forEach((reel, index) => {
      reel.spin(symbolData[index], allSymbols, this.textures);
    });
    
    // Schedule reel stops
    let stoppedReels = 0;
    this.reels.forEach((reel, index) => {
      setTimeout(() => {
        reel.stop();
        stoppedReels++;
        
        if (stoppedReels === this.reels.length) {
          this.handleSpinComplete(symbolData);
        }
      }, GAME_CONFIG.SPIN_DURATION + index * GAME_CONFIG.REEL_STOP_DELAY);
    });
  }
  
  /**
   * Spin with backend result (for displaying wins)
   */
  public spinWithResult(backendReels: number[][], winningLines: number[], winningSymbols: number[], linePayouts: any[]): void {
    if (this.spinning) return;
    
    this.spinning = true;
    this.paylines.clear();
    this.winAnimator.clear();
    
    // Convert backend reels to symbol data
    const symbolData = this.reelsToSymbolData(backendReels);
    const allSymbols = getAllSymbols().filter(s => this.textures.has(s.name));
    console.log(symbolData)
    console.log('Bac', backendReels);
    // Spin each reel with the backend result
    this.reels.forEach((reel, index) => {
      reel.spin(symbolData[index], allSymbols, this.textures);
    });
    
    // Schedule reel stops
    let stoppedReels = 0;
    this.reels.forEach((reel, index) => {
      setTimeout(() => {
        reel.stop();
        stoppedReels++;
        
        if (stoppedReels === this.reels.length) {
          this.handleSpinCompleteWithWins(symbolData, winningLines, winningSymbols, linePayouts);
        }
      }, GAME_CONFIG.SPIN_DURATION + index * GAME_CONFIG.REEL_STOP_DELAY);
    });
  }
  
  /**
   * Spin with random reels (for demo/free play)
   */
  public spin(): void {
    if (this.spinning) return;
    
    this.spinning = true;
    this.paylines.clear();
    this.winAnimator.clear();
    
    const allSymbols = getAllSymbols().filter(s => this.textures.has(s.name));
    const finalSymbols = this.generateFinalSymbols();
    
    this.reels.forEach((reel, index) => {
      reel.spin(finalSymbols[index], allSymbols, this.textures);
    });
    
    let stoppedReels = 0;
    this.reels.forEach((reel, index) => {
      setTimeout(() => {
        reel.stop();
        stoppedReels++;
        
        if (stoppedReels === this.reels.length) {
          this.handleSpinComplete(finalSymbols);
        }
      }, GAME_CONFIG.SPIN_DURATION + index * GAME_CONFIG.REEL_STOP_DELAY);
    });
  }
  
  private handleSpinComplete(finalSymbols: SymbolData[][]): void {
    this.spinning = false;
    
    // Note: Win evaluation is done by backend
    // We don't evaluate client-side for real play
    // Frontend evaluation is only for display purposes
    
    if (this.spinCompleteCallback) {
      this.spinCompleteCallback();
    }
  }
  
  private handleSpinCompleteWithWins(
    finalSymbols: SymbolData[][],
    winningLines: number[],
    winningSymbols: number[],
    linePayouts: any[]
  ): void {
    this.spinning = false;
    
    // Delay win animation to let reels settle
    setTimeout(() => {
      // Animate winning lines if there are any
      if (winningLines.length > 0 && linePayouts.length > 0) {
        const wins = this.createWinResults(finalSymbols, winningLines, winningSymbols, linePayouts);
        const totalWin = linePayouts.reduce((sum, payout) => sum + payout.payout, 0);
        this.winAnimator.animateWin(wins, this.reels as any, totalWin);
      }
      
      if (this.spinCompleteCallback) {
        this.spinCompleteCallback();
      }
    }, 500); // 500ms delay after reels stop
  }
  
  /**
   * Create win results for animation from backend data
   */
  private createWinResults(
    finalSymbols: SymbolData[][],
    winningLines: number[],
    winningSymbols: number[],
    linePayouts: any[]
  ): any[] {
    const { PAYLINES } = GAME_CONFIG;
    const wins: any[] = [];
    
    linePayouts.forEach(payout => {
      const lineIndex = payout.lineIndex;
      const payline = PAYLINES[lineIndex];
      
      // Get positions for this payline
      const positions: { reel: number; row: number }[] = payline.map(([col, row]) => ({
        reel: col,
        row: row
      }));
      
      wins.push({
        paylineIndex: lineIndex,
        payline: payline,
        symbolId: finalSymbols[0][0].id, // Use first symbol of the line
        matchCount: payout.count,
        multiplier: 1,
        winAmount: payout.payout,
        positions: positions
      });
    });
    
    return wins;
  }
  
  /**
   * Convert backend reel numbers to symbol data
   * Backend format: reels[row][column] with symbol IDs (1-8)
   */
  private reelsToSymbolData(backendReels: number[][]): SymbolData[][] {
    const allSymbols = getAllSymbols().filter(s => this.textures.has(s.name));
    const result: SymbolData[][] = [];
    
    // Backend reels are in format: reels[row][col] (3 rows × 5 cols)
    // We need to convert to: result[col][row] for each reel
    const rows = backendReels.length;
    const cols = backendReels[0]?.length || 5;
    
    console.log('=== REEL CONVERSION DEBUG ===');
    console.log('Backend reels (row-major):');
    backendReels.forEach((row, i) => console.log(`  Row ${i}:`, row));
    console.log(`Dimensions: ${rows} rows × ${cols} cols`);
    console.log('Available symbols:', allSymbols.map(s => `${s.id}(${s.name})`).slice(0, 8));
    
    for (let col = 0; col < cols; col++) {
      const reelSymbols: SymbolData[] = [];
      console.log(`\nReel ${col}:`);
      for (let row = 0; row < rows; row++) {
        const symbolId = backendReels[row][col];
        // Map backend symbol ID to frontend symbol
        const symbol = this.getSymbolByBackendId(symbolId, allSymbols);
        console.log(`  Position [${row}][${col}] = ID ${symbolId} → ${symbol.name}`);
        reelSymbols.push(symbol);
      }
      result.push(reelSymbols);
    }
    
    console.log('\nFrontend reels (column-major):');
    result.forEach((reel, idx) => {
      console.log(`  Reel ${idx}:`, reel.map(s => s.name));
    });
    
    return result;
  }
  
  /**
   * Map backend symbol ID to frontend symbol
   * Backend uses symbols 1-8 (configurable)
   * Frontend has 24 symbols, so we map backend IDs to the first N symbols
   */
  private getSymbolByBackendId(backendId: number, allSymbols: SymbolData[]): SymbolData {
    // Backend symbols start from 1, array index from 0
    const symbolIndex = backendId - 1;
    
    // Use first 8 symbols for backend IDs 1-8
    if (symbolIndex >= 0 && symbolIndex < 8 && symbolIndex < allSymbols.length) {
      const symbol = allSymbols[symbolIndex];
      return symbol;
    }
    
    // Fallback: use modulo to wrap around
    const fallbackIndex = backendId % allSymbols.length;
    console.warn(`Unknown backend symbol ID ${backendId}, using fallback index ${fallbackIndex}`);
    return allSymbols[fallbackIndex];
  }
  
  private generateFinalSymbols(): SymbolData[][] {
    const allSymbols = getAllSymbols().filter(s => this.textures.has(s.name));
    const result: SymbolData[][] = [];
    
    for (let reel = 0; reel < GAME_CONFIG.REELS_COUNT; reel++) {
      const reelSymbols: SymbolData[] = [];
      for (let row = 0; row < GAME_CONFIG.ROWS_COUNT; row++) {
        const randomIndex = Math.floor(Math.random() * allSymbols.length);
        reelSymbols.push(allSymbols[randomIndex]);
      }
      result.push(reelSymbols);
    }
    
    return result;
  }
  
  public update(delta: number): void {
    this.reels.forEach(reel => {
      reel.update(delta);
    });
    this.winAnimator.update(delta);
  }
  
  public setBet(bet: number): void {
    this.currentBet = bet;
  }
  
  public setPaylines(count: number): void {
    this.activePaylines = count;
  }
  
  public getCurrentBet(): number {
    return this.currentBet;
  }
  
  public getActivePaylines(): number {
    return this.activePaylines;
  }
  
  public isSpinning(): boolean {
    return this.spinning;
  }
  
  public onSpinComplete(callback: () => void): void {
    this.spinCompleteCallback = callback;
  }
}